import { useTranslation } from 'react-i18next';
import { humanKey } from '../i18n/index.ts';
import type { Verdict } from '../lib/types.ts';

export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return <span className={`badge badge-${status.toLowerCase()}`}>{t(`status.${status}`, status)}</span>;
}

export function ResultTag({ result }: { result: string }) {
  const { t } = useTranslation();
  return <span className={`tag tag-${result}`}>{t(`result.${result}`, result)}</span>;
}

/** PRD §4.1 (US-21): the route is always "intended", the classification "candidate" unless CONFIRMED, with the route
 * confidence in the same visual unit. The classification names the route and the CMC it would rest on: the basis CMC
 * if one is closed, otherwise every candidate still open. Without a CMC candidate (national route, route pending) there is
 * no classification to name, and only the intended route is shown. Route IDs are shown as the engine emits them; no display name
 * is invented for them (DECISIONS E-59). */
export function Classification({
  verdict,
}: {
  verdict: Pick<Verdict, 'reg_route' | 'route_confidence' | 'cmc_basis'>;
}) {
  const { t } = useTranslation();
  const confirmed = verdict.route_confidence === 'CONFIRMED';
  const basis = verdict.cmc_basis.filter((c) => c.basis);
  const cmcs = (
    basis.length > 0
      ? basis
      : verdict.cmc_basis.filter((c) => c.state !== 'eliminated' && c.state !== 'suspended')
  ).map((c) => c.cmc);
  const confidence = t(`confidence.${verdict.route_confidence}`, verdict.route_confidence);
  return (
    <span className="classification">
      <span className="muted">{t('classification.intended')}:</span> <strong>{verdict.reg_route}</strong>{' '}
      <span className={`confidence confidence-${verdict.route_confidence.toLowerCase()}`}>{confidence}</span>
      {cmcs.length > 0 && (
        <>
          <br />
          <span className="muted small">
            {confirmed ? t('classification.confirmed') : t('classification.candidate')}: {verdict.reg_route}{' '}
            {t('classification.via')} {cmcs.join(' / ')}, {confidence.toLowerCase()}
          </span>
        </>
      )}
    </span>
  );
}

/** A value from a closed engine or database vocabulary, translated when a key exists, otherwise shown as stored. */
export function Enum({ ns, v }: { ns: string; v: string }) {
  const { t } = useTranslation();
  return <>{t(`${ns}.${v}`, v)}</>;
}

/** US-22: on every surface that used an assumption. */
export function DemoStamp({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <p className="stamp" role="note">
      {t('stamp.demo', { n: count })}
    </p>
  );
}

export function Msg({ k }: { k: string }) {
  const { t } = useTranslation();
  return <>{t(k, { defaultValue: humanKey(k) })}</>;
}

export function Loading() {
  const { t } = useTranslation();
  return <p className="muted">{t('common.loading')}</p>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <p className="error" role="alert">
      {t('common.error')}: {error instanceof Error ? error.message : String(error)}
    </p>
  );
}

export function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(fmtValue).join('; ');
  return JSON.stringify(v);
}
